import React, { useEffect } from "react";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input, InputNumber, Popconfirm } from "antd";
import TextArea from "antd/es/input/TextArea";

import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import { SectionField } from "./HomeTabs";

interface locationPinItem {
  order: number;
  longitude: number;
  latitude: number;
}

export interface globalReachProps {
  heading?: string;
  subtext?: string;
  locationPin?: locationPinItem[];
}

interface Props {
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  onDelete: (index: number, field: SectionField) => void;
  form: any;
  globalReach?: globalReachProps;
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
}

const GlobalReach: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  globalReach,
  onDelete,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
}) => {
  useEffect(() => {
    if (!globalReach) return;

    if (mode === "list") {
      form.setFieldsValue({
        globalReach: {
          heading: globalReach?.heading || "",
          subtext: globalReach?.subtext || "",
          locationPin:
            globalReach.locationPin?.map((item) => ({
              order: item?.order,
              latitude: item?.latitude,
              longitude: item?.longitude,
            })) || [],
        },
      });
    }

    if (mode === "add") {
      form.setFieldsValue({
        globalReach: {
          locationPin: [{ order: 0, latitude: "", longitude: "" }],
        },
      });
    }

    if (
      mode === "edit" &&
      editingIndex !== null &&
      globalReach.locationPin?.[editingIndex]
    ) {
      const item = globalReach.locationPin[editingIndex];

      form.setFieldsValue({
        globalReach: {
          locationPin: [
            {
              order: item?.order,
              latitude: item?.latitude,
              longitude: item?.longitude,
            },
          ],
        },
      });
    }
  }, [mode, editingIndex, globalReach, form]);

  return (
    <div>
      {isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl mb-4">
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Heading"
              name={["globalReach", "heading"]}
              rules={[{ required: true, message: "Enter heading" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Subtext"
              name={["globalReach", "subtext"]}
              rules={[{ required: true, message: "Enter subtext" }]}
            >
              <TextArea rows={3} />
            </Form.Item>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                setIsEditing(false);
                handleCancel();
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="rounded-[20px] overflow-hidden hidden sm:block">
          <table className="min-w-full">
            <thead>
              <tr className="bg-primary text-white">
                <th className="py-3 px-4 text-left">Heading</th>
                <th className="py-3 px-4 text-left">Subtext</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>

            <tbody className="bg-[#F9F9F9]">
              {globalReach?.heading || globalReach?.subtext ? (
                <tr className="border-b">
                  <td className="px-4 py-3">{globalReach?.heading}</td>
                  <td className="px-4 py-3">{globalReach?.subtext}</td>
                  <td className="px-4 py-3 text-right">
                    <EditOutlined
                      className="text-primary text-xl cursor-pointer"
                      onClick={() => setIsEditing(true)}
                    />
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-6">
                    <Button type="dashed" onClick={() => setIsEditing(true)}>
                      Add Data
                    </Button>
                  </td>
                </tr>
              )}

              <tr>
                <td colSpan={4} className="px-4 py-3 text-right">
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setMode("add");
                      setEditingIndex(null);
                    }}                    
                    className="text-primary font-semibold"
                  >
                    Add Location
                  </Button>
                </td>
              </tr>

              <tr>
                <td colSpan={4} className="p-4">
                  <p className="font-semibold mb-3">Locations</p>

                  <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-2 text-left">Order</th>
                        <th className="px-4 py-2 text-left">Longitude</th>
                        <th className="px-4 py-2 text-left">Latitude</th>
                        <th className="px-4 py-2 text-right">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {globalReach?.locationPin?.length ? (
                        globalReach.locationPin.map((item, index) => (
                          <tr key={index} className="border-t">
                            <td className="px-4 py-2">{item?.order}</td>
                            <td className="px-4 py-2">{item?.longitude}</td>
                            <td className="px-4 py-2">{item?.latitude}</td>

                            <td className="px-4 py-2 text-right">
                              <div className="flex justify-end gap-4 text-lg">
                                <EditOutlined
                                  className="cursor-pointer text-primary"
                                  onClick={() => {
                                    setMode("edit");
                                    setEditingIndex(index);
                                  }}
                                />

                                <Popconfirm
                                  title="Are you sure to Delete this?"
                                  onConfirm={() =>
                                    onDelete(index, "globalReach")
                                  }
                                >
                                  <DeleteOutlined className="cursor-pointer text-red-500" />
                                </Popconfirm>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-6 text-center text-gray-500"
                          >
                            No location added
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="block sm:hidden mb-4">
          <div className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm space-y-4">
            <div><b className="text-primary">Heading:</b><div>
            <p>{globalReach?.heading || "—"}</p>
            </div>
          </div>

            <div>
              <div><b className="text-primary">Subtext:</b></div>
              <p>{globalReach?.subtext || "—"}</p>
            </div>

            <div>
              <EditOutlined
                onClick={() => setIsEditing(true)}
                className="cursor-pointer text-primary text-[20px]"
              />
            </div>
          </div>

          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => {
              setMode("add");
              setEditingIndex(null);
            }}
            className="text-primary font-semibold mt-3"
          >
            Add Location
          </Button>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="sm:hidden mt-4 space-y-4">
          {globalReach?.locationPin?.map((item: any, index: any) => (
            <div
              key={index}
              className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <div>
                <span>
                  <p className="font-semibold text-primary">Label:</p> {item?.order}
                </span>
              </div>
              <div>
                <span>
                  <p className="font-semibold text-primary">Longitude:</p> {item?.longitude}
                </span>
              </div>
              <div>
                <span>
                  <p className="font-semibold text-primary">Latitude:</p> {item?.latitude}
                </span>
              </div>

              <div className="flex justify-end gap-4 text-primary text-xl mt-3">
                <EditOutlined
                  className="cursor-pointer"
                  onClick={() => {
                    setMode("edit");
                    setEditingIndex(index);
                  }}
                />
                <Popconfirm
                  title="Are you sure to delete this?"
                  onConfirm={() => onDelete(index, "globalReach")}
                  okText="Yes"
                  cancelText="No"
                >
                  <DeleteOutlined className="cursor-pointer text-red-500" />
                </Popconfirm>
              </div>
            </div>
          ))}
        </div>
      )}

      {mode !== "list" && !isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name={["globalReach", "locationPin"]}>
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} className="border p-4 rounded mb-4">
                      <div className="flex gap-4">
                        <Form.Item
                          label="Order"
                          name={[field.name, "order"]}
                          rules={[{ required: true, message: "Enter Order" }]}
                          className="w-full"
                        >
                          <InputNumber style={{ width: "100%" }}/>
                        </Form.Item>
                      </div>

                      <div className="flex gap-4">
                        <Form.Item
                          label="Latitude"
                          name={[field.name, "latitude"]}
                          rules={[
                            { required: true, message: "Enter latitude" },
                          ]}
                          className="w-full"
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>

                        <Form.Item
                          label="Longitude"
                          name={[field.name, "longitude"]}
                          rules={[
                            { required: true, message: "Enter longitude" },
                          ]}
                          className="w-full"
                        >
                          <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                handleCancel();
                setMode("list");
                setEditingIndex(null);
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalReach;
